/**
 * The repositories linked to this account's key, read-only (ACT2-1133). Links are made and changed only
 * in Settings → Context: a link is keyed by database, not by account, so it outlives the account, and
 * this sheet could not show a link whose account is gone.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { projectPathLabel } from '../../lib/linkedRepoList';
import { projectTypeLabel } from '../../lib/projectType';

function statusLabel(status: string | undefined): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'generating':
      return 'Generating…';
    case 'failed':
      return 'Failed';
    case undefined:
    default:
      return 'Not indexed';
  }
}

export function WarehouseProjectsSection({ accountKey }: { accountKey: string }): React.JSX.Element {
  const orpc = useOrpc();
  const { wp } = useWorkspace();

  const { data, isPending, isError, error, refetch } = useQuery({
    ...orpc.warehouseProjects.list.queryOptions({ input: { accountKey } }),
    refetchInterval: (query) =>
      query.state.data?.doc?.status === 'generating' ||
      query.state.data?.projects.some((p) => p.docState?.status === 'generating')
        ? 5000
        : false,
  });
  const projects = data?.projects ?? [];
  const accountDoc = data?.doc ?? null;

  return (
    <section className="mt-6 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">Linked repositories</h3>
          <p className="text-xs text-muted-foreground">Projects that build docs for this account.</p>
        </div>
        <Link to={wp('/settings/context')} className="shrink-0 text-xs text-brand hover:underline">
          Manage in Settings → Context
        </Link>
      </div>

      {accountDoc && (
        <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
          <p className="text-sm font-medium text-foreground">Warehouse documentation</p>
          <p className="text-xs text-muted-foreground">
            {statusLabel(accountDoc.status)}
            {accountDoc.error ? ` — ${accountDoc.error}` : ''}
          </p>
          {accountDoc.status === 'ready' && (
            <Link
              to={wp(`/context/generated/${accountDoc.id}`)}
              className="mt-1 block text-xs text-primary hover:underline"
            >
              View in Context Hub
            </Link>
          )}
        </div>
      )}

      {isPending ? (
        <div role="status" aria-label="Loading linked repositories" className="flex flex-col gap-2">
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
        </div>
      ) : isError ? (
        <Callout variant="error">
          <p>Failed to load the linked repositories.</p>
          <p className="mt-1 text-xs">{error.message || 'Unknown error'}</p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            label="Retry"
            onClick={() => void refetch()}
          />
        </Callout>
      ) : projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">No repositories are linked to this account.</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {projects.map((project) => (
            <li key={project.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground" title={project.repoFullName}>
                  {project.repoFullName}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {projectPathLabel(project.projectPath)} · {projectTypeLabel(project.projectType)}
                </p>
              </div>
              {project.docState?.status === 'generating' ? (
                <Badge variant="info" className="shrink-0">
                  Generating docs
                </Badge>
              ) : project.accountStatus === 'disconnected' ? (
                <Badge variant="warning" className="shrink-0">
                  Disconnected
                </Badge>
              ) : (
                <Badge variant="success" className="shrink-0">
                  Connected
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
      {projects.some((project) => project.installationId === 0) && (
        <p className="text-xs text-muted-foreground">
          Personal-token linked projects do not refresh on a push. Refresh their docs from Settings → Context.
        </p>
      )}
    </section>
  );
}
